import type { ComponentType } from "react";
import React, { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import { Route, Routes } from "react-router-dom";
import { useIntl } from "react-intl";
import { useAtomValue } from "jotai";

import {
  useFeatureFlags,
  withIntlProvider,
  disableNavState,
  DevProdBanner,
  Loading,
  useOperatingContext,
} from "@granular/fabric3-core";
import { FabricApp } from "@granular/fabric3-definitions";
import { GKIconName } from "@granular/gds";

import Homepage from "./Homepage";
import NotFound from "./components/404";
import { GuardedApp } from "./components/GuardedApp";
import { ImpersonationContext } from "./components/ImpersonationContext";
import { FabricLayoutShell } from "./components/FabricLayoutShell";
import translations from "./i18n";
import { APP_ICON_IF_UNSPECIFIED, CONTAINER_WINDOW_NAME } from "./constants";
import Help from "./components/Help";
import Logout from "./components/Logout";
import { AppTracking } from "./components/AppTracking";
import { checkAppPermissions } from "./helpers";
import { getAppMetadata } from "./helpers/appData.helpers";
import { getNamespaceRoutes } from "./helpers/appRoutes";
import { getSidebarVisibility } from "./helpers/sidebarVisibilityFlags";
import { SearchImplementation } from "./components/Search";
import { StreamInitializer } from "./components/StreamInitializer";

const isLocalhost = import.meta.url.includes("localhost");
/**
 * Get app metadata, based off of app's package.json files
 */
const appMetadata = getAppMetadata(isLocalhost);

/**
 * Lazy-load root components found in an app's `apps/<namespace>/src/App.tsx` file.
 *
 * It's important that we build this _outside_ of a react component so that we're not trying to
 * reload lazy components on every render.
 */
const lazyApps = appMetadata.reduce<Record<string, FabricApp["component"]>>(
  (acc, [namespace]) => {
    if (!namespace) return acc;
    acc[namespace] = lazy(
      () =>
        import(`../../apps/${namespace}/src/App.tsx`) as Promise<{
          default: ComponentType<unknown>;
        }>,
    );
    return acc;
  },
  {},
);

/**
 * Give a name to the current tab so it we can switch between tabs, instead
 * of creating a new one everytime we click on the `Go to Insights/NRE` button
 * on either app. This name is unique and should not ever be reassigned.
 * Classic insights is aware of this name an calls its tab with it.
 * If you change it, it all falls apart, so please don't.
 */
window.name = CONTAINER_WINDOW_NAME;

/**
 * The Container is the main component of Fabric3. It is responsible for
 * rendering the layout shell, the core navigation, and the main content area
 * where apps are rendered.
 *
 * This is THE MAIN APP components for all of Fabric3.
 */
export const Container: React.FunctionComponent = () => {
  const flags = useFeatureFlags();
  const intl = useIntl();

  const { userCan } = useOperatingContext();

  /* ——————————————————————— Build App List ——————————————————————————— */

  /**
   * At this point, we take the list-of-lists above and transform it to two
   * additional targets. We'll start with the first and use it to make the
   * second. We could've done this above but beh.
   *
   * The first one is a meta list of build/runtime information on our apps.
   * We'll use this soon to tell the Container how to map a namespace to its
   * component. Note the typing here: we're turning Package Data to a 'real'
   * FabricApp 😎
   */
  const appRuntimeInfo: FabricApp[] = appMetadata
    /**
     * Use a `flatMap` since a `fabric3:sidebar` key is an array that a single
     * app/namespace can use to specify multiple routes that are displayed
     * in the Core Nav.
     */
    .flatMap(([namespace, packageData]) => {
      return packageData.fabric3?.sidebar?.map((sidebarItem) => {
        // For composed apps like `data` we can have distinct flags for `app-data-index` and `app-data-dashboard`
        const sidebarVisibilityFlags = getSidebarVisibility(
          flags,
          namespace,
          sidebarItem.route,
        );

        const hasPermission = checkAppPermissions(
          sidebarItem.permissionModel,
          userCan,
        );

        return {
          ...sidebarItem,
          namespace,
          label: intl.formatMessage({
            id: sidebarItem.label,
            defaultMessage: sidebarItem.label,
          }),
          /**
           * Add some ✨ Additional properties ✨
           *
           * Let's start with the _actual_ icon.
           */

          /**
           * Icons: we're being defensive in case people mistype an icon name.
           */
          icon: sidebarItem.icon
            ? GKIconName[sidebarItem.icon as keyof typeof GKIconName] ||
              APP_ICON_IF_UNSPECIFIED
            : APP_ICON_IF_UNSPECIFIED,

          /**
           * Small app badge that we _may_ display next to a label. Note that we
           * use `pnpm monorepo:check-package-json` to validate the shape and
           * values here.
           */
          ...(sidebarItem.badge && {
            badge: {
              label: intl.formatMessage({ id: sidebarItem.badge.label }),
              color: sidebarItem.badge.color,
            },
          }),

          /**
           * This has a bit of nuance in terms of:
           *
           * a) How GDS expects the route,
           * b) How React Router expects this route, and
           * c) How individual apps can split routing.
           */
          to: sidebarItem.route
            ? `${namespace}/${sidebarItem.route}`
            : `${namespace}`,

          /**
           * The all-important Namespace 👉 Component mapping!
           */
          component: lazyApps[namespace]!,

          /**
           * Visibility and routability are two different things, both managed
           * via feature-flag state.
           *
           * Visibility (sidebar item): gated on BOTH the FF and the user's
           * permission — no point showing a nav item for an app the user
           * can't access.
           *
           * Routability (URL access): gated on the FF only. The GuardedApp
           * component enforces permission at render time so direct URL access
           * is also protected — flag off → ComingSoon, flag on but no
           * permission → PermissionMessage.
           *
           * isLocalhost bypasses FF gates for local development.
           *
           * https://fabric3.granular.ag/docs/additional-topics/core-navigation#visibility-and-routability
           */
          visible:
            isLocalhost || (hasPermission && sidebarVisibilityFlags.visible),
          routable: isLocalhost || sidebarVisibilityFlags.routable,

          /**
           * Insert permissions if they are defined
           */
          permissionModel: sidebarItem.permissionModel,
        };
      });
    });

  // The Mobile app uses this to turn off the navigation bar entirely through
  // a query param. They wanted to handle navigation with a native component.
  const disableNav = useAtomValue(disableNavState);

  // All done! Now we just mostly draw things 🎨

  /* ——————————————————————— Render Container ——————————————————————————— */

  // Find CARL chatbot app for rendering outside of routes. This enables the pop up
  // bot dialog when not in the chat app.
  const carlChatbotApp = appRuntimeInfo.find((app) =>
    app.namespace.includes("carl-chatbot"),
  );
  const CarlChatbot = carlChatbotApp?.component ?? undefined;

  return (
    <>
      <Helmet>
        <title>Granular Insights</title>
        <style type="text/css">{`.gk-main-nav {display: ${
          disableNav ? "none" : "inherit"
        }}`}</style>
      </Helmet>
      {/**
       * Loud warning when the local dev container is pointed at production
       * data (`pnpm dev:prod`). Renders nothing in any other case.
       */}
      <DevProdBanner />
      {/**
       * This suspense wraps FabricLayoutShell so that we can fetch data
       * from within that component that may throw a promise. If you need to
       * call useOperatingContext (which uses useSuspenseQuery), you must
       * do that in <FabricLayoutShell /> and _not_ directly here in <Container />
       */}
      <Suspense fallback={<Loading />}>
        <FabricLayoutShell
          alertsOverlay
          home={{ to: "/" }}
          appRuntimeInfo={appRuntimeInfo}
          isLocalhost={isLocalhost}
        >
          {/**
           * This second suspense should catch promises within main content area.
           * We need two because we don't want the loader to cover the sidebar if the promise is thrown within this scope.
           * FabricLayoutShell is itself wrapped to catch what is above here.
           */}
          <Suspense fallback={<Loading />}>
            <ImpersonationContext />
            <AppTracking />
            <Routes>
              {/*
              Use the list we generated at Container mount to map each
              App to its route, which is simply its folder name in apps/

              ⚠️ Apps with multiple routes should have a single "namespace" route here
              — which is exactly what `getNamespaceRoutes` guarantees. It used to
              be a convention nobody enforced, so `apps/data` registered
              `data/*` twice and its first sidebar item's flag silently gated
              both of its pages.

              That's it.

              DO NOT OVERTHINK THIS.
              IT SHOULD BE THIS SIMPLE.
              THIS SHOULD NOT REQUIRE EXCEPTIONS.
              */}
              {getNamespaceRoutes(appRuntimeInfo).map((appInfo) => (
                <Route
                  key={`fabric3-app-${appInfo.namespace}`}
                  /**
                   * Ok so the top two props are easy-peasy. This one has a
                   * small nuance. We add a `/*` to the `path` prop below
                   * because this is the ONLY way in which you can tell
                   * React Router to match 🌟nested routes🌟 that come from
                   * your app.
                   *
                   * If you did not do this, you'd have to specify the full
                   * relative path to a component/view in your app:
                   *
                   *    /my-app/foo
                   *
                   *    /my-app/foo/bar/:id
                   *
                   * Instead of just setting up your router in `App.tsx`
                   * like this:
                   *
                   *    foo
                   *
                   *    foo/bar/:id
                   *
                   * One fewer thing for you to think about 🌸🥰 And this
                   * means that you can rename your app's namespace from
                   * `my-app` to `my-other-app` and things will just work.
                   * That's not really as much of an advantage as you
                   * writing less and worrying less about specifying the
                   * full relative path ✨
                   */
                  path={`${appInfo.namespace}/*`}
                  element={<GuardedApp appInfo={appInfo} />}
                />
              ))}

              {/*
              These are some special routes we (the Architecture Team) actually
              do specify by hand. These are things like global 404 pages,
              what to do when the user clicks "Log Out", default homepage
              routes, etc. Container-wide stuff you'd expect a Core team to
              do you know?
            */}
              <Route path="/help" element={<Help />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/" element={<Homepage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            {CarlChatbot && <CarlChatbot />}
          </Suspense>
          <SearchImplementation />
          {CarlChatbot && <StreamInitializer />}
        </FabricLayoutShell>
      </Suspense>
    </>
  );
};

/**
 * We finally export the Container after wrapping it in an I18n context. See
 * the `Shell` component in the Core for why we do this here and not in the
 * Core.
 */
const PolyglotContainer = withIntlProvider(Container, {
  translations,
});

export default PolyglotContainer;
